import type { ScopelyIdentity } from "@scopely/web-sdk-scopely-account";
import {
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from "react";

// API Methods:
import { IScopelyIdentityApi, useInitAPI } from "./api";
// Types:
import { ConnectionStatus, ScopelyIdentityContext } from "./types";
// Other:
import { handlePushLogout } from "components/PWA/helpers/handlePushLogout";
import { useFacebookContext } from "contexts/FacebookProvider";
import { useLinks } from "hooks/useLinks";
import { useNavigate, useSearchParams } from "react-router-dom";
import createPersistedState from "use-persisted-state";
import { LOGIN_ACTION } from "./api/login";
import { LOGOUT_ACTION, LOGOUT_NOT_CONNECTED_ACTION } from "./api/logout";
import { createScopelyIdentity } from "./configv2";
import { getScopelyStateFromSession } from "./helpers";

const useSIDAccessTokenState = createPersistedState<string | null>(
	"store.solitairetripeaks.com:sidAccessToken"
);

const useSIDIdentityTokenState = createPersistedState<string | null>(
	"store.solitairetripeaks.com:sidIdentityToken"
);

const useScopelyIdentityTokenState = createPersistedState<string | null>(
	"com.scopely:scopelyid:idToken"
);

const ScopelyIdentityProvider = ({ children }: { children: ReactNode }) => {
	const [redirectTo, setRedirectTo] = useState<string | undefined>();
	const [connectionStatus] = useState<ConnectionStatus>(
		ConnectionStatus.UNKNOWN
	);
	const links = useLinks();

	const [sidIdentityToken, setSIDIdentityToken] =
		useSIDIdentityTokenState(null);
	const [sidAccessToken, setSIDAccessToken] = useSIDAccessTokenState(null);
	const [scopelyIdentityToken, setScopelyIdentityToken] =
		useScopelyIdentityTokenState(null);

	const navigate = useNavigate();
	const [scopelyIdentity, setScopelyIdentity] =
		useState<ScopelyIdentity | null>(null);
	const handledScopelyRedirectState = useRef<string | null>(null);
	const hasInitializedScopelyIdentity = useRef(false);

	const resetScopelyIdentity = useCallback(
		async (clearPkce = true, tenant?: string) => {
			if (clearPkce) {
				sessionStorage.removeItem("lastPkceChallengeGenerated");
			}

			setScopelyIdentity(null);
			try {
				const nextScopelyIdentity = await createScopelyIdentity(tenant);
				setScopelyIdentity(nextScopelyIdentity);
				if (process.env.REACT_APP_WITH_DEBUG === "true") {
					console.log(
						"Scopely Identity API config",
						nextScopelyIdentity.getApiConfig()
					);
				}
			} catch (error) {
				console.error("Error initializing Scopely Identity:", error);
			}
		},
		[]
	);

	useEffect(() => {
		if (hasInitializedScopelyIdentity.current) {
			return;
		}
		hasInitializedScopelyIdentity.current = true;
		resetScopelyIdentity(false);
	}, [resetScopelyIdentity]);

	const api: IScopelyIdentityApi = useInitAPI({
		scopelyIdentity,
		setSIDAccessToken,
		setSIDIdentityToken,
		setScopelyIdentityToken
	});

	const onSuccessLogout =
		(path = links.home.path) =>
		async () => {
			// Remove ?state= param left by IdP redirect before navigating
			const url = new URL(window.location.href);
			if (url.searchParams.has("state")) {
				url.searchParams.delete("state");
				window.history.replaceState(null, "", url.toString());
			}
			navigate(redirectTo ?? path, { replace: true });
			await handlePushLogout();
		};

	const onSuccess =
		(path = links.home.path) =>
		() => {
			navigate(redirectTo ?? path, { replace: true });
		};
	const [searchParams] = useSearchParams();
	const { fbAccessToken, logout } = useFacebookContext();

	const removePreviousLogin = () => {
		localStorage.removeItem("store.solitairetripeaks.com:authToken");
		localStorage.removeItem("store.solitairetripeaks.com:profileImage");
		localStorage.removeItem("store.solitairetripeaks.com:userName");
		fbAccessToken && logout();
	};

	// Handle Redirect from Scopely
	useEffect(() => {
		const searchParamState = searchParams.get("state");
		if (
			searchParamState === null ||
			!scopelyIdentity ||
			handledScopelyRedirectState.current === searchParamState
		) {
			return;
		}
		handledScopelyRedirectState.current = searchParamState;

		const tryConcludeScopelyLoginAction = async () => {
			let stateObj = getScopelyStateFromSession();
			stateObj = stateObj && JSON.parse(stateObj);
			stateObj = stateObj || { action: searchParamState };

			removePreviousLogin();
			if (stateObj?.action === LOGIN_ACTION) {
				//await api.tryConcludeLogin(onSuccess(stateObj?.path));
				await api.loginRedirect(onSuccess(stateObj?.path));
			} else if (
				stateObj?.action === LOGOUT_ACTION ||
				stateObj?.action === LOGOUT_NOT_CONNECTED_ACTION
			) {
				//await api.tryConcludeLogout(onSuccess(stateObj?.path));
				await api.logoutRedirect(onSuccessLogout(stateObj?.path));
			}
		};

		tryConcludeScopelyLoginAction().catch((error) => {
			console.error("Error concluding Scopely login/logout:", error);
		});
	}, [api, scopelyIdentity, searchParams]);

	return (
		<ScopelyIdentityContext.Provider
			value={{
				connectionStatus: connectionStatus,
				isConnected: connectionStatus === ConnectionStatus.CONNECTED,
				isScopelyIdentityReady: Boolean(scopelyIdentity),
				initiateLogin: api.initiateLogin,
				loginRedirect: api.loginRedirect,
				initiateLogout: api.initiateLogout,
				logoutRedirect: api.logoutRedirect,
				resetScopelyIdentity,
				startLogin: api.startLogin,
				tryConcludeLogin: api.tryConcludeLogin,
				startLogout: api.startLogout,
				tryConcludeLogout: api.tryConcludeLogout,
				setScopelyAccessToken: setSIDAccessToken,
				setScopelyIdentityToken: setSIDIdentityToken,
				setScopelyIdToken: setScopelyIdentityToken,
				sidAccessToken: sidAccessToken,
				sidIdentityToken: sidIdentityToken,
				scopelyIdentityToken: scopelyIdentityToken,
				setRedirectTo
			}}
		>
			{children}
		</ScopelyIdentityContext.Provider>
	);
};

const useScopelyIdentityContext = () => {
	const context = useContext(ScopelyIdentityContext);
	if (context === undefined) {
		throw new Error(
			"useScopelyIdentityContext was used outside of its Provider"
		);
	}
	return context;
};

export default ScopelyIdentityProvider;
export { useScopelyIdentityContext };
